/**
 * CustomerQuickViewModal.tsx
 * Component Popup / Quick View hiển thị thông tin nhanh về khách hàng ngay trên các trang (ví dụ /tai-lieu-minh-hoa).
 *
 * Tính năng chính:
 * - Header thông tin cá nhân: Avatar, Tên, Mã KH, Nhãn (VIP/Tiềm năng...), SĐT, Email, Ngày sinh, CCCD, Địa chỉ, Nghề nghiệp.
 * - Quick Actions: Tạo minh họa mới, Chỉnh sửa thông tin, Xem chi tiết toàn màn hình M16.
 * - Tabs bên dưới:
 *   1. 📝 Nhật ký & Ghi chú CRM
 *   2. 📄 Tài liệu minh họa (Xem/Sửa/Tải PDF)
 *   3. 👨‍👩‍👧‍👦 Người phụ thuộc
 */

import React, { useState, useMemo } from 'react';
import {
  Modal,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  TouchableWithoutFeedback,
} from 'react-native';
import { Icon, type IconName } from './Icon';
import { colors, spacing, fontWeights, borderRadius } from '../theme';
import {
  findCustomerById,
  type CustomerFull,
  type CustomerIllustration,
  type CustomerNote,
} from '../data/customers';
import { LABEL_META } from '../screens/M15CustomerListScreen';
import {
  CustomerNotesTab,
  CustomerIllustrationsTab,
  CustomerDependentsTab,
  CustomerInfoTab,
} from './customerTabs';

export interface CustomerQuickViewModalProps {

  visible: boolean;
  customerId: string | null;
  onClose: () => void;
  onViewIllustration?: (illustration: CustomerIllustration) => void;
  onEditIllustration?: (illustration: CustomerIllustration) => void;
  onDownloadPDF?: (illustration: CustomerIllustration) => void;
  onCreateNewIllustration?: (customerName: string, customerAge: number, customerGender: 'Nam' | 'Nữ') => void;
  onViewFullDetail?: (customerId: string) => void;
  onEditCustomer?: (customer: CustomerFull) => void;
}

const STAGE_META: Record<string, { label: string; color: string; bg: string }> = {
  new: { label: 'Mới tạo', color: '#2563EB', bg: '#EFF6FF' },
  negotiating: { label: 'Đang tư vấn', color: '#D97706', bg: '#FFFBEB' },
  closed: { label: 'Đã chốt', color: '#00A651', bg: '#F0FDF4' },
};

const NOTE_TYPE_META: Record<CustomerNote['type'], { label: string; icon: IconName; color: string; bg: string }> = {
  journal: { label: 'Nhật ký tư vấn', icon: 'description', color: '#003366', bg: 'rgba(0, 51, 102, 0.08)' },
  gift: { label: 'Tặng quà chăm sóc', icon: 'favorite', color: '#D97706', bg: 'rgba(217, 119, 6, 0.08)' },
  claims: { label: 'Hỗ trợ sự kiện BH', icon: 'medical_services', color: '#E11D48', bg: 'rgba(225, 29, 72, 0.08)' },
};


const RELATIONSHIP_LABEL: Record<string, string> = {
  spouse: 'Vợ/Chồng',
  child: 'Con',
  parent: 'Bố/Mẹ',
  other: 'Khác',
};

export const CustomerQuickViewModal: React.FC<CustomerQuickViewModalProps> = ({
  visible,
  customerId,
  onClose,
  onViewIllustration,
  onEditIllustration,
  onDownloadPDF,
  onCreateNewIllustration,
  onViewFullDetail,
  onEditCustomer,
}) => {
  const [activeTab, setActiveTab] = useState<number>(0);

  const customer = useMemo(() => {
    if (!customerId) return undefined;
    return findCustomerById(customerId);
  }, [customerId]);

  if (!visible || !customerId) return null;

  const TABS = [
    { key: 'notes', label: 'Nhật ký', icon: 'comment', count: customer?.notes.length || 0 },
    { key: 'illustrations', label: 'Minh họa', icon: 'description', count: customer?.illustrations.length || 0 },
    { key: 'dependents', label: 'Người phụ thuộc', icon: 'groups', count: customer?.dependents.length || 0 },
    { key: 'info', label: 'Chi tiết', icon: 'person', count: null },
  ];


  return (
    <View style={styles.backdrop}>
      <TouchableOpacity
        style={StyleSheet.absoluteFill}
        activeOpacity={1}
        onPress={onClose}
      />
      <View style={styles.modalCard}>

              {/* Modal Header */}
              <View style={styles.modalHeader}>
                <View style={styles.modalHeaderLeft}>
                  <Icon name="person" size={20} color={colors.primary} />
                  <Text style={styles.modalHeaderTitle} numberOfLines={1}>
                    {customer ? `Khách hàng: ${customer.name}` : 'Thông tin khách hàng'}
                  </Text>
                  {customer && onViewFullDetail && (
                    <TouchableOpacity
                      style={styles.headerSubtleLink}
                      onPress={() => {
                        onClose();
                        onViewFullDetail(customer.id);
                      }}
                      accessibilityLabel="Xem chi tiết khách hàng"
                    >
                      <Text style={styles.headerSubtleLinkText}>(Xem chi tiết)</Text>
                      <Icon name="chevron_right" size={14} color={colors.onSurfaceVariant} />
                    </TouchableOpacity>
                  )}
                </View>
                <TouchableOpacity style={styles.closeBtn} onPress={onClose} accessibilityLabel="Đóng popup">
                  <Icon name="close" size={20} color={colors.onSurfaceVariant} />
                </TouchableOpacity>
              </View>


              {!customer ? (
                <View style={styles.notFoundContainer}>
                  <Icon name="error" size={40} color={colors.onSurfaceVariant} />
                  <Text style={styles.notFoundText}>Không tìm thấy dữ liệu cho mã KH: {customerId}</Text>
                </View>
              ) : (
                <ScrollView style={styles.scrollBody} showsVerticalScrollIndicator={true}>
                  {/* Customer Hero Profile */}
                  <View style={styles.profileSection}>
                    <View style={[styles.avatar, { backgroundColor: customer.gender === 'Nữ' ? '#EC4899' : '#00A651' }]}>
                      <Text style={styles.avatarText}>{customer.name.charAt(0)}</Text>
                    </View>

                    <View style={styles.profileDetails}>
                      <View style={styles.profileTitleRow}>
                        <Text style={styles.customerName}>{customer.name}</Text>
                        <Text style={styles.customerIdTag}>Mã: {customer.id}</Text>
                      </View>

                      {customer.labels && customer.labels.length > 0 && (
                        <View style={styles.labelRow}>
                          {customer.labels.map((lbl) => (
                            <View key={lbl} style={[styles.labelTag, { backgroundColor: LABEL_META[lbl]?.bg || '#EFF6FF' }]}>
                              <Text style={[styles.labelTagText, { color: LABEL_META[lbl]?.color || colors.primary }]}>
                                ⭐ {LABEL_META[lbl]?.label || lbl}
                              </Text>
                            </View>
                          ))}
                        </View>
                      )}

                      <View style={styles.infoGrid}>
                        <View style={styles.infoItem}>
                          <Icon name="badge" size={14} color={colors.onSurfaceVariant} />
                          <Text style={styles.infoText}>{customer.occupation}</Text>
                        </View>
                        <View style={styles.infoItem}>
                          <Icon name="calendar_today" size={14} color={colors.onSurfaceVariant} />
                          <Text style={styles.infoText}>{customer.dob} ({customer.age} tuổi)</Text>
                        </View>
                        <View style={styles.infoItem}>
                          <Icon name={customer.gender === 'Nữ' ? 'female' : 'male'} size={14} color={colors.onSurfaceVariant} />
                          <Text style={styles.infoText}>{customer.gender}</Text>
                        </View>
                        <View style={styles.infoItem}>
                          <Icon name="phone" size={14} color={colors.onSurfaceVariant} />
                          <Text style={styles.infoText}>{customer.phone}</Text>
                        </View>
                        <View style={styles.infoItem}>
                          <Icon name="mail" size={14} color={colors.onSurfaceVariant} />
                          <Text style={styles.infoText}>{customer.email}</Text>
                        </View>
                        {customer.idNumber && (
                          <View style={styles.infoItem}>
                            <Icon name="badge" size={14} color={colors.onSurfaceVariant} />
                            <Text style={styles.infoText}>CCCD: {customer.idNumber}</Text>
                          </View>
                        )}
                      </View>
                    </View>
                  </View>


                  {/* Tabs Navigation */}
                  <View style={styles.tabHeaderContainer}>
                    <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.tabScrollView}>
                      {TABS.map((tab, idx) => {
                        const active = activeTab === idx;
                        return (
                          <TouchableOpacity
                            key={tab.key}
                            style={[styles.tabItem, active && styles.tabItemActive]}
                            onPress={() => setActiveTab(idx)}
                          >
                            <Icon name={tab.icon as IconName} size={15} color={active ? colors.primary : colors.onSurfaceVariant} />
                            <Text style={[styles.tabItemText, active && styles.tabItemTextActive]}>
                              {tab.label}
                              {tab.count !== null ? ` (${tab.count})` : ''}
                            </Text>
                          </TouchableOpacity>
                        );
                      })}
                    </ScrollView>
                  </View>

                  {/* Tab Contents */}
                  <View style={styles.tabContentContainer}>
                    {activeTab === 0 && <CustomerNotesTab notes={customer.notes} />}
                    {activeTab === 1 && (
                      <CustomerIllustrationsTab
                        illustrations={customer.illustrations}
                        onView={(i) => {
                          onClose();
                          onViewIllustration?.(i);
                        }}
                        onEdit={(i) => {
                          onClose();
                          onEditIllustration?.(i);
                        }}
                        onDownloadPDF={onDownloadPDF}
                      />
                    )}
                    {activeTab === 2 && <CustomerDependentsTab dependents={customer.dependents} />}
                    {activeTab === 3 && <CustomerInfoTab customer={customer} />}
                  </View>

                </ScrollView>
              )}
            </View>
    </View>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    position: 'fixed' as any,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.65)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing[4],
    zIndex: 9999,
  },
  modalCard: {
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.lg,
    width: '100%',
    maxWidth: 680,
    maxHeight: '90%',
    overflow: 'hidden',
    shadowColor: '#000000',
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.25,
    shadowRadius: 20,
    elevation: 10,
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing[5],
    paddingVertical: spacing[4],
    backgroundColor: '#f8fafc',
    borderBottomWidth: 1,
    borderBottomColor: colors.outlineVariant,
  },
  modalHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[2],
  },
  modalHeaderTitle: {
    fontSize: 16,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
  },
  headerSubtleLink: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
    marginLeft: spacing[2],
  },
  headerSubtleLinkText: {
    fontSize: 12,
    color: colors.onSurfaceVariant,
    fontWeight: fontWeights.medium,
  },

  closeBtn: {

    padding: spacing[1],
    borderRadius: borderRadius.full,
  },
  notFoundContainer: {
    padding: spacing[10],
    alignItems: 'center',
    gap: spacing[3],
  },
  notFoundText: {
    fontSize: 14,
    color: colors.onSurfaceVariant,
  },
  scrollBody: {
    padding: spacing[5],
  },

  /* PROFILE HERO */
  profileSection: {
    flexDirection: 'row',
    gap: spacing[4],
    padding: spacing[4],
    backgroundColor: '#f8fafc',
    borderRadius: borderRadius.md,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    marginBottom: spacing[4],
  },
  avatar: {
    width: 54,
    height: 54,
    borderRadius: 27,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarText: {
    color: '#ffffff',
    fontSize: 22,
    fontWeight: fontWeights.bold,
  },
  profileDetails: {
    flex: 1,
  },
  profileTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing[1],
  },
  customerName: {
    fontSize: 17,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
  },
  customerIdTag: {
    fontSize: 12,
    color: colors.onSurfaceVariant,
    fontWeight: fontWeights.semibold,
  },
  labelRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing[1.5],
    marginBottom: spacing[2.5],
  },
  labelTag: {
    paddingHorizontal: spacing[2],
    paddingVertical: 2,
    borderRadius: borderRadius.full,
  },
  labelTagText: {
    fontSize: 11,
    fontWeight: fontWeights.semibold,
  },
  infoGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing[2],
  },
  infoItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[1],
    marginRight: spacing[2],
  },
  infoText: {
    fontSize: 12,
    color: colors.onSurfaceVariant,
  },

  /* ACTION BAR */
  actionsBar: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing[2],
    marginBottom: spacing[4],
  },
  actionBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[1.5],
    paddingHorizontal: 14,
    paddingVertical: spacing[2],
    borderRadius: borderRadius.DEFAULT,
  },
  actionBtnPrimary: {
    backgroundColor: colors.primary,
  },
  actionBtnTextPrimary: {
    color: '#ffffff',
    fontWeight: fontWeights.bold,
    fontSize: 12,
  },
  actionBtnOutline: {
    backgroundColor: '#eff6ff',
    borderWidth: 1,
    borderColor: '#3b82f6',
  },
  actionBtnTextOutline: {
    color: colors.primary,
    fontWeight: fontWeights.semibold,
    fontSize: 12,
  },
  actionBtnSecondary: {
    backgroundColor: '#fff7ed',
    borderWidth: 1,
    borderColor: '#f59e0b',
  },
  actionBtnTextSecondary: {
    color: '#d97706',
    fontWeight: fontWeights.semibold,
    fontSize: 12,
  },

  /* TABS HEADER */
  tabHeaderContainer: {
    borderBottomWidth: 1,
    borderBottomColor: colors.outlineVariant,
    marginBottom: spacing[4],
  },
  tabScrollView: {
    flexDirection: 'row',
  },
  tabItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[1.5],
    paddingVertical: spacing[2.5],
    paddingHorizontal: 14,
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  tabItemActive: {
    borderBottomColor: colors.primary,
  },
  tabItemText: {
    fontSize: 13,
    color: colors.onSurfaceVariant,
    fontWeight: fontWeights.medium,
  },
  tabItemTextActive: {
    color: colors.primary,
    fontWeight: fontWeights.bold,
  },

  /* TAB CONTENT */
  tabContentContainer: {
    paddingBottom: spacing[4],
  },
  tabPanel: {
    gap: spacing[3],
  },
  emptyBox: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing[8],
    gap: spacing[2],
  },
  emptyText: {
    fontSize: 13,
    color: colors.onSurfaceVariant,
  },

  /* NOTES TAB */
  noteCard: {
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.DEFAULT,
    padding: 14,
    borderWidth: 1,
    borderColor: colors.outlineVariant,
    borderLeftWidth: 4,
    gap: spacing[1.5],
  },
  noteHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  noteBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[1],
    paddingHorizontal: spacing[2],
    paddingVertical: 2,
    borderRadius: borderRadius.full,
  },
  noteBadgeText: {
    fontSize: 11,
    fontWeight: fontWeights.bold,
  },
  noteDate: {
    fontSize: 11,
    color: colors.onSurfaceVariant,
  },
  noteBody: {
    fontSize: 13,
    color: colors.onSurface,
    lineHeight: 18,
  },
  noteAuthor: {
    fontSize: 11,
    color: colors.onSurfaceVariant,
    fontStyle: 'italic',
    alignSelf: 'flex-end',
  },

  /* ILLUSTRATION TAB */
  illustCard: {
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.DEFAULT,
    padding: 14,
    borderWidth: 1,
    borderColor: colors.outlineVariant,
    gap: spacing[2.5],
  },
  illustCardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  illustTitle: {
    fontSize: 13,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
  },
  illustId: {
    fontSize: 11,
    color: colors.onSurfaceVariant,
    marginTop: 2,
  },
  stageBadge: {
    paddingHorizontal: spacing[2],
    paddingVertical: 2,
    borderRadius: borderRadius.full,
  },
  stageBadgeText: {
    fontSize: 11,
    fontWeight: fontWeights.bold,
  },
  illustStatsRow: {
    flexDirection: 'row',
    backgroundColor: '#f8fafc',
    padding: spacing[2.5],
    borderRadius: borderRadius.DEFAULT,
    justifyContent: 'space-around',
  },
  illustStatItem: {
    alignItems: 'center',
  },
  illustStatVal: {
    fontSize: 13,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
  },
  illustStatLbl: {
    fontSize: 10,
    color: colors.onSurfaceVariant,
  },
  illustCardActions: {
    flexDirection: 'row',
    gap: spacing[2],
    justifyContent: 'flex-end',
  },
  miniActionBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: spacing[2.5],
    paddingVertical: spacing[1],
    borderRadius: borderRadius.full,
    borderWidth: 1,
  },
  miniViewBtn: {
    backgroundColor: '#EFF6FF',
    borderColor: '#3B82F6',
  },
  miniEditBtn: {
    backgroundColor: '#FFF7ED',
    borderColor: '#F59E0B',
  },
  miniPdfBtn: {
    backgroundColor: '#FEF2F2',
    borderColor: '#EF4444',
  },
  miniActionText: {
    fontSize: 11,
    fontWeight: fontWeights.semibold,
  },

  /* DEPENDENTS TAB */
  dependentCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[3],
    padding: spacing[3],
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.DEFAULT,
    borderWidth: 1,
    borderColor: colors.outlineVariant,
  },
  dependentAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  dependentAvatarText: {
    color: '#ffffff',
    fontWeight: fontWeights.bold,
    fontSize: 16,
  },
  dependentHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  dependentName: {
    fontSize: 14,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
  },
  relBadge: {
    backgroundColor: '#F3F4F6',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: borderRadius.full,
  },
  relBadgeText: {
    fontSize: 11,
    color: colors.onSurfaceVariant,
    fontWeight: fontWeights.medium,
  },
  dependentSub: {
    fontSize: 12,
    color: colors.onSurfaceVariant,
    marginTop: 2,
  },
  dependentOcc: {
    fontSize: 11,
    color: colors.onSurfaceVariant,
    marginTop: 2,
  },

  /* FULL INFO TAB */
  infoBlock: {
    backgroundColor: '#f8fafc',
    borderRadius: borderRadius.DEFAULT,
    padding: spacing[4],
    borderWidth: 1,
    borderColor: colors.outlineVariant,
    gap: spacing[2],
  },
  infoBlockTitle: {
    fontSize: 14,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
    marginBottom: spacing[1],
  },
  infoRowSimple: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: spacing[1],
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: '#e2e8f0',
  },
  infoRowLbl: {
    fontSize: 12,
    color: colors.onSurfaceVariant,
  },
  infoRowVal: {
    fontSize: 12,
    fontWeight: fontWeights.semibold,
    color: colors.onSurface,
  },
});
